import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { DialogDocumentsOverviewFile } from 'app/dialogs/documents/documents-overview-file/dialog-documents-overview-file';
import { base64ToArrayBuffer, saveByteArray } from 'app/helpers/blobDownload';
import { formatSsn } from 'app/helpers/formatSsn';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListDocumentRes } from 'app/interfaces/responses/List/listDocumentRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { PensionService } from 'app/services/api/pension/pension.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { catchError, map, of as observableOf, startWith, switchMap } from 'rxjs';

@Component({
    selector: 'app-documents-overview',
    templateUrl: './documents-overview.component.html',
    styleUrls: ['./documents-overview.component.scss'],
})
export class DocumentsOverviewComponent implements OnInit {
    //#region Globals

    userDetails: ExposedReadUserDetailsRes = null;

    userRoles = USER_ROLES;

    // Table
    pageSize: number = 10;
    pageNumber: number = 0;
    pageSizes = [5, 10, 25, 50, 100];
    dataSource = new MatTableDataSource<ListDocumentRes>();
    docData: ListDocumentRes[];
    totalData: number = 0;
    tableColumns: string[] = [];
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // Form
    form: UntypedFormGroup;
    isLoading = true;
    typesDropDown: Array<DropDownItem> = [];

    //#endregion

    //#region Init

    constructor(
        private formBuilder: UntypedFormBuilder,
        private skjalamidja: SkjalamidjaService,
        private route: ActivatedRoute,
        private alertsService: AlertsService,
        private pensionService: PensionService,
        public dialog: MatDialog,
        public lsrauthService: LsrauthService,
        private loaderService: LoaderService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.initTableColumns();
        this.initForm();
        this.initDropdowns();
    }

    initForm() {
        let dateFrom = new Date();
        dateFrom.setMonth(dateFrom.getMonth() - 4);

        this.form = this.formBuilder.group({
            searchString: new UntypedFormControl(''),
            dateFrom: new UntypedFormControl(new Date(dateFrom.toDateString()), [Validators.required]),
            dateTo: new UntypedFormControl(new Date(new Date().toDateString()), [Validators.required]),
            documentTypeId: new UntypedFormControl(null),
        });
    }

    ngAfterViewInit() {
        this.fetchData();
    }

    fetchData() {
        this.pageSize = 10;
        this.pageNumber = 0;
        this.dataSource.paginator = this.paginator;
        this.paginator.pageIndex = 0;

        this.paginator.page
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoading = true;
                    return this.fetchDocuments(this.paginator.pageIndex, this.paginator.pageSize, true).pipe(
                        catchError(() => observableOf(null))
                    );
                }),
                map((docData) => {
                    if (docData == null) return [];
                    this.isLoading = false;
                    return docData;
                })
            )
            .subscribe((docData) => {
                this.docData = docData;
                if (docData.length > 0) {
                    this.totalData = docData[0].totalNumberOfEntries;
                } else {
                    this.totalData = 0;
                }

                this.dataSource = new MatTableDataSource(this.docData);
            });
    }

    fetchDocuments(pageNumber: number, pageSize: number, scrollToTop = false) {
        let dateFrom = new Date(this.form.get('dateFrom').value);
        dateFrom.setUTCHours(0, 0, 0, 0);
        let dateTo = new Date(this.form.get('dateTo').value);
        dateTo.setUTCHours(23, 59, 59, 999);

        return this.skjalamidja.listDocuments({
            dateFrom: dateFrom,
            dateTo: dateTo,
            documentCategoryId: null,
            documentId: null,
            documentSubcategoryId: null,
            documentSystemId: null,
            documentTypeId: this.form.get('documentTypeId').value == 'Allt' ? null : this.form.get('documentTypeId').value,
            fileExtension: null,
            fileName: null,
            Ssn: null,
            stafraentIslandId: null,
            title: null,
            // We determine if the user is typing in a SSN, and if so we always remove type symbol '-'.
            searchString:
                this.form.get('searchString').value == ''
                    ? null
                    : isNaN(+this.form.get('searchString').value?.replace('-', ''))
                      ? this.form.get('searchString').value
                      : this.form.get('searchString').value?.replace('-', ''),
            skip: pageNumber * pageSize,
            take: pageSize,
        });
    }

    initDropdowns() {
        var [categoriesRes, subcategoriesRes, systemsRes, typesRes, departmentsRes, documentTemplates] =
            this.route.snapshot.parent.data.dropdownData;

        typesRes.forEach((x) => {
            this.typesDropDown.push({
                value: x.documentTypeId,
                viewValue: x.title,
            });
        });
    }

    //#endregion

    //#region Table functions

    initTableColumns() {
        if (this.lsrauthService.inRole(this.userDetails, this.userRoles.EMPLOYEE_LSR)) {
            this.tableColumns = ['documentDate', 'title', 'typeTitle', 'subcategoryTitle', 'ssn', 'details'];
        } else {
            this.tableColumns = ['documentDate', 'title', 'typeTitle', 'subcategoryTitle'];
        }
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.pageNumber = event.pageIndex;
        this.fetchDocuments(this.pageNumber, this.pageSize, true);
    }

    //#endregion

    //#region Select particular document

    getDocument(doc: ListDocumentRes) {
        if (doc.fileExtension === 'paycheck_web') {
            this.pensionService.getPdfPensionPaycheck(true, doc.documentSystemExternalReference).then((fileRes) => {
                if (fileRes) {
                    const byteArray = base64ToArrayBuffer(fileRes.base64File);
                    saveByteArray(doc?.fileName.replace('paycheck_web', 'pdf'), byteArray);
                }
            });
        } else {
            this.skjalamidja
                .readDocument(
                    {
                        DocumentId: doc.documentId,
                        AzureLocation: null,
                        StafraentIslandId: null,
                    },
                    true
                )
                .subscribe((fileRes) => {
                    var sampleArr = base64ToArrayBuffer(fileRes.base64File);
                    saveByteArray(doc.fileName, sampleArr);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Skjalinu hefur verið hlaðið niður í vafrann þinn');
                    this.alertsService.changeAlertSuccessShow(true);
                });
        }
    }

    viewDocumentDetails(doc: ListDocumentRes) {
        const dialogRef = this.dialog.open(DialogDocumentsOverviewFile, {
            data: {
                autoFocus: false,
                restoreFocus: false,
                disableClose: true,
                document: doc,
            },
            width: '600px',
        });
        dialogRef.afterClosed().subscribe((result) => {});

        dialogRef.componentInstance.getDocument.subscribe((_doc) => {
            this.getDocument(_doc);
        });

        dialogRef.componentInstance.fetchData.subscribe((_doc) => {
            this.fetchData();
        });
    }

    //#endregion

    //#region Helper methods

    formatSsn(s) {
        return formatSsn(s);
    }

    //#endregion
}
